import { Button, Col, Drawer, Input, Row } from "antd";
import { Icon, Table } from "components";
import React, { Component } from "react";
import Dropzone from "react-dropzone";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import Strings from "utils/strings";
import { toast } from "utils/utils";
import Compressor from "compressorjs";
import "./styles.scss";

import placeholder from "assets/images/placeholders/user.jpg";

class BlogAuthors extends Component<any, any> {
    constructor(props: any) {
        super(props);

        this.state = {
            blogAuthors: [],
            hasUnsavedFields: false,
            loading: false,
        };
    }

    componentDidMount() {
        const { dispatch } = this.props;

        dispatch(setTitle(Strings.sidebar.blogPosts));

        delayedDispatch(
            setBreadcrumb(() => {
                return {
                    locations: [
                        {
                            text: Strings.sidebar.settings,
                            route: "/settings",
                            icon: "preferences",
                        },
                        {
                            text: Strings.sidebar.blogPosts,
                            icon: "testimonial",
                        },
                    ],
                };
            })
        );

        this.getBlogAuthors();
    }

    componentDidUpdate() {
        const { dispatch } = this.props;
        dispatch(updateCrumb());
    }

    getBlogAuthors = async () => {
        const response = await API.get({
            url: Endpoints.uriBlogPostAuthors(),
        });

        if (response.ok) {
            this.setState({ blogAuthors: response.data.results.authors || [] });
        }
    };

    patchBlogAuthor = async (original: any) => {
        const { blogAuthors } = this.state;

        const response = await API.patch({
            url: Endpoints.uriBlogPostAuthors(original._id),
            data: {
                active: !original.active,
            },
        });

        if (response.ok) {
            const updatedBlogAuthors = blogAuthors.map((author: any) => {
                if (author._id === original._id) {
                    return { ...author, active: !author.active };
                }
                return author;
            });

            this.setState({ blogAuthors: updatedBlogAuthors });
        }
    };

    isValid = () => {
        const { tempAuthor } = this.state;

        if (!tempAuthor?.name?.trim() || !tempAuthor?.photo) {
            return false;
        }

        return true;
    };

    saveBlogAuthor = async () => {
        const { tempAuthor, blogAuthors } = this.state;

        if (!this.isValid()) {
            return toast.warn(Strings.errors.invalidFields);
        }

        this.setState({ loading: true });

        const formData = new FormData();
        formData.append("name", tempAuthor.name);
        formData.append("position", tempAuthor.position || "");
        formData.append("linkedIn", tempAuthor.linkedIn || "");

        if (tempAuthor?.photo && typeof tempAuthor.photo === "object") {
            formData.append("photo", tempAuthor.photo.file);
        } else {
            formData.append("photo", tempAuthor.photo);
        }

        formData.append("active", tempAuthor.active || true);

        const response = tempAuthor._id ? API.put : API.post;

        try {
            const apiResponse = await response({
                url: Endpoints.uriBlogPostAuthors(tempAuthor._id ?? ""),
                data: formData,
            });

            if (apiResponse.ok) {
                if (!tempAuthor._id) {
                    blogAuthors.push(apiResponse.data.results.author);
                } else {
                    const updatedBlogAuthorIndex = blogAuthors.findIndex(
                        (author: any) => author._id === tempAuthor._id
                    );

                    if (updatedBlogAuthorIndex !== -1) {
                        blogAuthors[updatedBlogAuthorIndex] = apiResponse.data.results.author;
                    }
                }

                this.setState({
                    blogAuthors: blogAuthors,
                    hasUnsavedFields: false,
                    showAdd: false,
                    tempAuthor: {},
                });

                toast.success(Strings.blogPosts.authorSaved);
            } else {
                toast.error(Strings.blogPosts.somethingWentWrong);
            }
        } catch (error) {
            console.log("error", error);
        }

        this.setState({ loading: false });
    };

    getBase64 = (file: any) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    onDrop = (files: any, field: string) => {
        if (!field) return;

        try {
            const file = files[files.length - 1];

            new Compressor(file, {
                mimeType: "image/png",
                success: (result: any) => {
                    this.getBase64(result).then((res) => {
                        this.setState((state: any) => ({
                            tempAuthor: {
                                ...state.tempAuthor,
                                photo: { file: result, preview: res },
                            },
                            hasUnsavedFields: true,
                        }));
                    });
                },
            });
        } catch (err) {
            toast.warning(Strings.errors.notSupportedFile);
        }
    };

    onAdd() {
        this.setState({ showAdd: true, tempAuthor: {} });
    }

    onClose() {
        this.setState({ showAdd: false, tempAuthor: {} });
    }

    renderSidebar() {
        const { showAdd, tempAuthor, loading, hasUnsavedFields } = this.state;

        let photo = tempAuthor?.photo;

        if (photo && typeof photo === "object") {
            photo = photo.preview;
        } else if (!photo) {
            photo = placeholder;
        }

        return (
            <Drawer
                visible={showAdd}
                onClose={() => this.onClose()}
                width={500}
                title={tempAuthor?._id ? Strings.blogPosts.editAuthor : Strings.blogPosts.addAuthor}
                footer={
                    <div className="DrawerFooter">
                        <Button
                            type="primary"
                            onClick={() => {
                                this.saveBlogAuthor();
                            }}
                            loading={loading}
                            disabled={!hasUnsavedFields}
                        >
                            {Strings.generic.save}
                        </Button>
                        <Button
                            type="default"
                            onClick={() => {
                                this.onClose();
                            }}
                        >
                            {Strings.generic.cancel}
                        </Button>
                    </div>
                }
                placement="right"
            >
                <Row gutter={[20, 20]}>
                    <Col xs={24}>
                        <label className="SingleLabel __required">{Strings.fields.photo}</label>
                        <div className="ImageContainer">
                            <div className="Image">
                                <img alt={Strings.fields.image} src={photo} />
                                <div className="ImageOverlay">
                                    <Dropzone
                                        accept="image/jpg, image/jpeg, image/png"
                                        className="ImageOverlayOption"
                                        onDrop={(files) => this.onDrop(files, "icon")}
                                    >
                                        <Icon name="pencil-outline" />
                                    </Dropzone>
                                    <div
                                        className={`ImageOverlayOption${
                                            Boolean(tempAuthor?.photo) ? "" : " __disabled"
                                        }`}
                                        onClick={() => {
                                            this.setState((state: any) => ({
                                                tempAuthor: {
                                                    ...state.tempAuthor,
                                                    photo: null,
                                                },
                                                hasUnsavedFields: true,
                                            }));
                                        }}
                                    >
                                        <Icon name="trash" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24}>
                        <label className="SingleLabel __required">{Strings.fields.name}</label>
                        <Input
                            placeholder={Strings.fields.name}
                            value={tempAuthor?.name || ""}
                            onChange={(e) => {
                                const value = e.target.value;

                                this.setState((state: any) => ({
                                    tempAuthor: { ...state.tempAuthor, name: value },
                                    hasUnsavedFields: true,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={24}>
                        <label className="SingleLabel">{Strings.blogPosts.position}</label>
                        <Input
                            placeholder={Strings.blogPosts.position}
                            value={tempAuthor?.position || ""}
                            onChange={(e) => {
                                const value = e.target.value;

                                this.setState((state: any) => ({
                                    tempAuthor: { ...state.tempAuthor, position: value },
                                    hasUnsavedFields: true,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={24}>
                        <label className="SingleLabel">{Strings.projects.linkLinkedIn}</label>
                        <Input
                            placeholder={Strings.projects.linkLinkedIn}
                            value={tempAuthor?.linkedIn || ""}
                            onChange={(e) => {
                                const value = e.target.value;

                                this.setState((state: any) => ({
                                    tempAuthor: { ...state.tempAuthor, linkedIn: value },
                                    hasUnsavedFields: true,
                                }));
                            }}
                        />
                    </Col>
                </Row>
            </Drawer>
        );
    }

    render() {
        const { blogAuthors } = this.state;

        return (
            <React.Fragment>
                <Helmet>
                    <title>{Strings.sidebar.blogPosts}</title>
                    <meta name="description" content="Description of Blog Posts" />
                </Helmet>
                <Table
                    title={{
                        icon: "testimonial",
                        title: Strings.sidebar.blogPosts,
                    }}
                    data={blogAuthors}
                    columns={[
                        {
                            Header: Strings.fields.photo,
                            id: "photo",
                            accessor: (row: any) => row.photo,
                            type: "image",
                            maxWidth: 65,
                        },
                        {
                            Header: Strings.fields.name,
                            id: "name",
                            accessor: (row: any) => row.name,
                        },
                        {
                            Header: Strings.blogPosts.position,
                            id: "position",
                            accessor: (row: any) => row.position || "-",
                        },
                    ]}
                    paginated={false}
					isSinglePage
					filterable={false}
                    add={{
                        label: String(
                            Strings.formatString(Strings.generic.addNewM, Strings.blogPosts.author.toLowerCase())
                        ),
                        onClick: () => this.onAdd(),
                    }}
                    actions={{
                        edit: (original, value) => ({
                            onClick: () => {
                                this.setState({ showAdd: true, tempAuthor: JSON.parse(JSON.stringify(original)) });
                            },
                        }),
                        toggle: (original, value) => ({
                            value: original.active,
                            onChange: () => this.patchBlogAuthor(original),
                        }),
                    }}
                />
                {this.renderSidebar()}
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: any) => ({});

export default connect(mapStateToProps)(BlogAuthors);
